import { create } from 'zustand';
import type { PriceListDto, PriceListInput, PriceListVariantDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * Trade price lists (ADR-045): a percentage off every retail price, and exact
 * prices where staff set them. The list being priced keeps its own page of
 * variants, searched on the server like every other list.
 */

interface PriceListsState {
  items: PriceListDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  search: string;

  /** The variants of the list being priced. */
  variants: PriceListVariantDto[];
  variantsCursor: string | null;
  variantsLoading: boolean;
  variantSearch: string;
  exactOnly: boolean;

  load: (search?: string) => Promise<void>;
  loadMore: () => Promise<void>;
  create: (input: PriceListInput) => Promise<boolean>;
  update: (id: string, input: Partial<PriceListInput>) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;

  loadVariants: (listId: string, filters?: { search?: string; exactOnly?: boolean }) => Promise<void>;
  loadMoreVariants: (listId: string) => Promise<void>;
  setExact: (listId: string, variantId: string, price: number) => Promise<boolean>;
  clearExact: (listId: string, variantId: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const usePriceListsStore = create<PriceListsState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  search: '',

  variants: [],
  variantsCursor: null,
  variantsLoading: false,
  variantSearch: '',
  exactOnly: false,

  async load(search) {
    const next = search ?? get().search;
    set({ loading: true, error: null, search: next });
    try {
      const page = await withSession(() => adminApi.priceLists.list({ limit: PAGE_SIZE, search: next || undefined }));
      set({ items: page.items, nextCursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the price lists.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, search, items } = get();
    if (!nextCursor || loadingMore) return;
    set({ loadingMore: true });
    try {
      const page = await withSession(() =>
        adminApi.priceLists.list({ limit: PAGE_SIZE, cursor: nextCursor, search: search || undefined }),
      );
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more price lists.') });
    }
  },

  async create(input) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.priceLists.create(input));
      set({ saving: false });
      await get().load();
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not create the price list.') });
      return false;
    }
  },

  async update(id, input) {
    set({ saving: true, error: null });
    try {
      const updated = await withSession(() => adminApi.priceLists.update(id, input));
      set((state) => ({ saving: false, items: state.items.map((row) => (row.id === id ? updated : row)) }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save the price list.') });
      return false;
    }
  },

  async remove(id) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.priceLists.remove(id));
      set((state) => ({ saving: false, items: state.items.filter((row) => row.id !== id) }));
      return true;
    } catch (error) {
      // "Trade accounts buy from this list" comes back from the API in words.
      set({ saving: false, error: errorMessage(error, 'Could not delete the price list.') });
      return false;
    }
  },

  async loadVariants(listId, filters) {
    const search = filters?.search ?? get().variantSearch;
    const exactOnly = filters?.exactOnly ?? get().exactOnly;
    set({ variantsLoading: true, error: null, variantSearch: search, exactOnly });
    try {
      const page = await withSession(() =>
        adminApi.priceLists.variants(listId, {
          limit: PAGE_SIZE,
          search: search || undefined,
          exactOnly: exactOnly || undefined,
          locale: useUiStore.getState().locale,
        }),
      );
      set({ variants: page.items, variantsCursor: page.meta.nextCursor, variantsLoading: false });
    } catch (error) {
      set({ variantsLoading: false, error: errorMessage(error, 'Could not load the prices.') });
    }
  },

  async loadMoreVariants(listId) {
    const { variantsCursor, variantsLoading, variantSearch, exactOnly, variants } = get();
    if (!variantsCursor || variantsLoading) return;
    set({ variantsLoading: true });
    try {
      const page = await withSession(() =>
        adminApi.priceLists.variants(listId, {
          limit: PAGE_SIZE,
          cursor: variantsCursor,
          search: variantSearch || undefined,
          exactOnly: exactOnly || undefined,
          locale: useUiStore.getState().locale,
        }),
      );
      set({ variants: [...variants, ...page.items], variantsCursor: page.meta.nextCursor, variantsLoading: false });
    } catch (error) {
      set({ variantsLoading: false, error: errorMessage(error, 'Could not load more prices.') });
    }
  },

  async setExact(listId, variantId, price) {
    set({ error: null });
    try {
      await withSession(() => adminApi.priceLists.setItem(listId, variantId, price));
      set((state) => ({
        variants: state.variants.map((row) => (row.variantId === variantId ? { ...row, exactPrice: price, price } : row)),
      }));
      return true;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not save the price.') });
      return false;
    }
  },

  async clearExact(listId, variantId) {
    set({ error: null });
    try {
      await withSession(() => adminApi.priceLists.removeItem(listId, variantId));
      set((state) => ({
        variants: state.variants.map((row) =>
          row.variantId === variantId ? { ...row, exactPrice: null, price: row.listPrice } : row,
        ),
      }));
      return true;
    } catch (error) {
      set({ error: errorMessage(error, 'Could not remove the price.') });
      return false;
    }
  },
}));
